import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

import { PermissionsService } from '@app/login/_services/permissions.service';
import { SubjectVersionService } from '@services/subject-version.service';
import { AccountingDocumentService } from '@app/accounting_documents/_services/accounting-document.service';
import { SearchSubjectVersion } from '@models/search-subject-version.model';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';
import { prepareDateForDb } from '@app/_utils/date-util';
import { generatePdf } from '@app/_utils/pdf-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';

import { CorporateClientBillingService } from './corporate-client-billing.service';
import { AccDocModificationModel, IssuedCorporateClientBillingResult } from './corporate-client-billing.model';

@Component({
    selector: 'app-corporate-client-billing',
    templateUrl: './corporate-client-billing.component.html',
    styleUrls: ['./corporate-client-billing.component.css']
})
export class CorporateClientBillingComponent implements OnInit {

    private readonly ISSUED_STATUS_CODE = 'ISSUED';

    modificationModels: AccDocModificationModel[] = [];
    accDocFoundLabel: string | null = null;
    accDocNotFound = false;
    accDocInvalidStatus = false;
    submitting = false;
    showValidations = false;
    issuedResult: IssuedCorporateClientBillingResult | null = null;
    isDownloadingPdf = false;
    isDownloadingProtocolPdf = false;

    issueForm = this.formBuilder.group({
        dateFrom: [null, Validators.required],
        dateTo: [null, Validators.required],
        subjectVersion: [null, Validators.required],
        accDocNumber: [null, Validators.required],
        modificationModelId: [null, Validators.required]
    });

    subjectVersionsSearch: OperatorFunction<string, SearchSubjectVersion[]> = (input$: Observable<string>) =>
        input$.pipe(
            debounceTime(300),
            switchMap(input =>
                this.subjectVersionService.filterSubjectVersions(input).pipe(
                    catchError(() => of([]))
                )
            )
        );

    constructor(
        public perms: PermissionsService,
        private formBuilder: FormBuilder,
        private subjectVersionService: SubjectVersionService,
        private accountingDocumentService: AccountingDocumentService,
        private corporateClientBillingService: CorporateClientBillingService,
        private uiEvent: UIEventCustom,
        private router: Router,
        private toastr: ToastrService,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        this.corporateClientBillingService.getModificationModels().subscribe(
            models => this.modificationModels = models,
            err => displayError(err)
        );
    }

    subjectVersionFormatter(element: any): string {
        if (!element) {
            return '';
        }
        return element.identNum ? `${element.fullName} (${element.identNum})` : element.fullName;
    }

    onAccDocNumberBlur(): void {
        const value = this.issueForm.get('accDocNumber')?.value;
        this.accDocFoundLabel = null;
        this.accDocNotFound = false;
        this.accDocInvalidStatus = false;
        if (!value) {
            this.issuedResult = null;
            return;
        }
        this.accountingDocumentService.findInvoiceByNum(value).subscribe(
            results => {
                if (results && results.length > 0) {
                    const doc: any = results[0];
                    const recipient = doc?.recepient?.fullName ?? doc?.recipientFullName ?? '';
                    this.accDocFoundLabel = recipient ? `${doc.invoiceNumber} — ${recipient}` : doc.invoiceNumber;
                    this.accDocNotFound = false;
                    this.accDocInvalidStatus = doc?.status?.code !== this.ISSUED_STATUS_CODE;
                } else {
                    this.accDocFoundLabel = null;
                    this.accDocNotFound = true;
                }
            },
            err => {
                this.accDocFoundLabel = null;
                this.accDocNotFound = true;
                displayError(err);
            }
        );
    }

    issue(): void {
        this.showValidations = true;
        if (this.issueForm.invalid || this.accDocNotFound || this.accDocInvalidStatus) {
            return;
        }
        const formValue = this.issueForm.value;
        const subjectVersion: any = formValue.subjectVersion;
        if (!subjectVersion?.id) {
            return;
        }

        this.submitting = true;
        this.issuedResult = null;
        this.corporateClientBillingService.issue({
            fromDate: prepareDateForDb(formValue.dateFrom),
            toDate: prepareDateForDb(formValue.dateTo),
            subjectVersionId: subjectVersion.id,
            accDocNumber: formValue.accDocNumber,
            modificationModelId: formValue.modificationModelId
        }).pipe(
            finalize(() => this.submitting = false)
        ).subscribe(
            result => {
                this.issuedResult = result;
                this.uiEvent.displayUISuccess();
            },
            err => {
                const code: string | undefined = err?.error?.code;
                if (code && code.startsWith('DAP')) {
                    this.toastr.error(this.translate.instant('errorCodes.' + code));
                } else {
                    displayErrorFromUnknown(err);
                    this.uiEvent.displayUIError();
                }
            }
        );
    }

    viewIssuedDoc(): void {
        if (this.issuedResult) {
            this.router.navigate(['/add-edit-accounting-document'], { queryParams: { id: this.issuedResult.id } });
        }
    }

    async downloadProtocolPdf(): Promise<void> {
        if (!this.issuedResult) {
            return;
        }
        this.isDownloadingProtocolPdf = true;
        try {
            const blob = await this.corporateClientBillingService.generateProtocolPdf(this.issuedResult.id).toPromise();
            generatePdf('ППП-' + this.issuedResult.invoiceNumber, blob);
        } catch (err) {
            displayErrorFromUnknown(err);
        } finally {
            this.isDownloadingProtocolPdf = false;
        }
    }

    async downloadIssuedDoc(): Promise<void> {
        if (!this.issuedResult) {
            return;
        }
        this.isDownloadingPdf = true;
        try {
            const blob = await this.accountingDocumentService.generatePdf(this.issuedResult.id).toPromise();
            generatePdf(this.issuedResult.invoiceNumber, blob);
        } catch (err) {
            displayErrorFromUnknown(err);
        } finally {
            this.isDownloadingPdf = false;
        }
    }

    get f() {
        return this.issueForm.controls;
    }
}
